import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Chip } from '@mui/material';
import { BeachAccess } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestGetTimeOffSummary } from '../redux/sagas/requests/timeoff';
import { formatDayShort } from '../utils/dateUtils';

const TIME_OFF_TYPES = ['Vacation', 'Sick', 'Personal', 'Bereavement', 'Other'];

const formatDate = (d) => formatDayShort(d);

const formatRange = (start, end) => {
    const s = formatDate(start);
    const e = formatDate(end);
    if (!s) return e;
    if (!e || s === e) return s;
    return `${s} – ${e}`;
};

const SummaryGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const SummaryCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.surface.inset,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
}));

const SummaryValue = styled(Typography, { shouldForwardProp: (prop) => prop !== 'tone' })(({ theme, tone }) => ({
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.size.xlm,
    color: tone === 'muted' ? theme.palette.text.secondary : theme.palette.primary.main,
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const BreakdownSection = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
}));

const BreakdownRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
}));

const BreakdownLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
}));

const BreakdownValue = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
}));

const UpcomingSection = styled(Box)(({ theme }) => ({
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),
}));

const UpcomingList = styled(Box)({
    maxHeight: 200,
    overflowY: 'auto',
});

const UpcomingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
}));

const UpcomingMeta = styled(Box)({
    minWidth: 0,
});

const UpcomingDates = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.md,
    color: theme.palette.text.primary,
}));

const UpcomingType = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
}));

const StatusChip = styled(Chip)(({ theme }) => ({
    height: 20,
    fontSize: theme.typography.size.xs,
    flexShrink: 0,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '150px',
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(BeachAccess)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const TimeOffSummaryWidget = ({ widgetId, config = {}, onRemove }) => {
    const userId = useSelector((state) => state.auth.userId);

    const { data, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestGetTimeOffSummary(userId),
        cacheKey: `timeoff-summary-${userId}`,
        enabled: !!userId,
    });

    const thisYear = data?.thisYear || { year: new Date().getUTCFullYear(), byType: {}, total: 0 };
    const lastYear = data?.lastYear || { year: new Date().getUTCFullYear() - 1, byType: {}, total: 0 };

    const breakdown = TIME_OFF_TYPES
        .map((t) => ({ type: t, days: thisYear.byType?.[t] || 0 }))
        .filter((row) => row.days > 0);

    const upcoming = data?.upcoming || [];
    const upcomingTitle = data?.upcomingDays ? `Booked Time Off · Next ${data.upcomingDays} Days` : 'Booked Time Off';

    const isEmpty = (thisYear.total || 0) === 0 && (lastYear.total || 0) === 0 && upcoming.length === 0;

    return (
        <BaseWidget
            title="Time Off"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!isLoading && isEmpty ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">No time off recorded</Typography>
                </EmptyState>
            ) : (
                <>
                    <SummaryGrid>
                        <SummaryCard>
                            <SummaryValue>{thisYear.total || 0}</SummaryValue>
                            <SummaryLabel>Days Off This Year ({thisYear.year})</SummaryLabel>
                        </SummaryCard>
                        <SummaryCard>
                            <SummaryValue tone="muted">{lastYear.total || 0}</SummaryValue>
                            <SummaryLabel>Days Off Last Year ({lastYear.year})</SummaryLabel>
                        </SummaryCard>
                    </SummaryGrid>

                    {breakdown.length > 0 && (
                        <BreakdownSection>
                            <SectionTitle>This Year by Type</SectionTitle>
                            {breakdown.map((row) => (
                                <BreakdownRow key={row.type}>
                                    <BreakdownLabel>{row.type}</BreakdownLabel>
                                    <BreakdownValue>{row.days} {row.days === 1 ? 'day' : 'days'}</BreakdownValue>
                                </BreakdownRow>
                            ))}
                        </BreakdownSection>
                    )}

                    <UpcomingSection>
                        <SectionTitle>{upcomingTitle}</SectionTitle>
                        {upcoming.length === 0 ? (
                            <BreakdownLabel>Nothing booked</BreakdownLabel>
                        ) : (
                            <UpcomingList>
                                {upcoming.map((entry) => (
                                    <UpcomingRow key={entry._id}>
                                        <UpcomingMeta>
                                            <UpcomingDates>{formatRange(entry.startDate, entry.endDate)}</UpcomingDates>
                                            <UpcomingType>{entry.type}{entry.days ? ` · ${entry.days} working day${entry.days === 1 ? '' : 's'}` : ''}</UpcomingType>
                                        </UpcomingMeta>
                                        <StatusChip
                                            label={entry.status === 'Pending' ? 'Awaiting approval' : 'Approved'}
                                            size="small"
                                            color={entry.status === 'Pending' ? 'warning' : 'success'}
                                        />
                                    </UpcomingRow>
                                ))}
                            </UpcomingList>
                        )}
                    </UpcomingSection>
                </>
            )}
        </BaseWidget>
    );
};

export default TimeOffSummaryWidget;
